import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPrice } from '../utils/currency';

function ProductCard({ product }) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const imageSrc = useMemo(
    () => (product?.image?.src ? product.image.src : '/placeholder-image.jpg'),
    [product]
  );

  useEffect(() => {
    if (!imageSrc) {
      setIsImageLoaded(false);
      return;
    }

    // Reset for the new asset, but mark fallback placeholders as already ready.
    const isFallback = imageSrc === '/placeholder-image.jpg';
    setIsImageLoaded(isFallback);

    if (typeof window === 'undefined' || isFallback) {
      return;
    }

    const preloader = new window.Image();
    const handleReady = () => setIsImageLoaded(true);

    preloader.addEventListener('load', handleReady);
    preloader.addEventListener('error', handleReady);
    preloader.src = imageSrc;

    if (preloader.complete) {
      handleReady();
    }

    return () => {
      preloader.removeEventListener('load', handleReady);
      preloader.removeEventListener('error', handleReady);
    };
  }, [imageSrc]);

  const getPrice = () => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (variants.length === 0) {
      return 'No variants available';
    }

    const validVariant = variants
      .filter((variant) => typeof variant?.price === 'number')
      .reduce((best, current) => {
        if (!best) return current;
        return current.price < best.price ? current : best;
      }, null);

    if (!validVariant) {
      return 'Price unavailable';
    }

    const basePrice = validVariant.discount_price ?? validVariant.price;
    return formatPrice(basePrice);
  };

  if (!product) {
    return null;
  }

  return (
    <div className="product-card">
      <div className={`product-card-image${isImageLoaded ? ' product-card-image--loaded' : ''}`}>
        {!isImageLoaded && (
          <div className="product-card-image__placeholder" aria-hidden="true" />
        )}
        <Link to={`/product/${product._id}`}>
          <img 
            src={imageSrc} 
            alt={product.title || 'Product'} 
            className="product-card-image__img"
            loading="lazy"
            onLoad={() => setIsImageLoaded(true)}
            onError={() => setIsImageLoaded(true)}
          />
        </Link>
      </div>
      
      <div className="product-card-content">
        <Link to={`/product/${product._id}`}>
          <h3 className="product-title">
            {product.title || 'Product Title'}
          </h3>
        </Link>
            
        <div className="product-actions">
          <span className="product-price">{getPrice()}</span>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
